import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import { ClientTypeTag } from '../components/SoftTag';
import { useI18n } from '../i18n';
import { formatDate } from '../utils/formatters';
import { brandSemantic } from '../config/brand';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

export default function ContactsListPage({
  contacts,
  currentUser,
  contactFilter,
  setContactFilter,
  contactTypeFilter,
  setContactTypeFilter,
  contactDateRange,
  setContactDateRange,
  leads,
  suppliers,
  openLeadProfile,
  openSupplierProfile,
  showModal,
  canDelete = true,
}) {
  const { t } = useI18n();
  const [contactSearch, setContactSearch] = useState('');

  const filteredContacts = useMemo(() => contacts.filter((contact) => {
    if (contactFilter === 'my' && contact.userId !== currentUser.id) {
      return false;
    }
    if (contactTypeFilter !== 'all' && contact.type !== contactTypeFilter) {
      return false;
    }
    const [fromDate, toDate] = contactDateRange || [];
    if (fromDate && contact.date < fromDate.format('YYYY-MM-DD')) {
      return false;
    }
    if (toDate && contact.date > toDate.format('YYYY-MM-DD')) {
      return false;
    }
    if (contactSearch.trim()) {
      const query = contactSearch.trim().toLowerCase();
      if (
        !contact.client.toLowerCase().includes(query) &&
        !contact.subject.toLowerCase().includes(query) &&
        !contact.userName.toLowerCase().includes(query) &&
        !String(contact.type || '').toLowerCase().includes(query) &&
        !String(contact.referredBy || '').toLowerCase().includes(query) &&
        !String(contact.eventName || '').toLowerCase().includes(query) &&
        !String(contact.networkName || '').toLowerCase().includes(query)
      ) {
        return false;
      }
    }
    return true;
  }), [contactDateRange, contactFilter, contactSearch, contactTypeFilter, contacts, currentUser.id]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Email':
        return <MailOutlined style={{ color: brandSemantic.success }} />;
      case 'Phone':
        return <PhoneOutlined style={{ color: brandSemantic.info }} />;
      case 'Network':
      case 'Fair/Event':
      case 'Referral':
        return <FileOutlined style={{ color: brandSemantic.warning }} />;
      case 'Other':
        return <FileOutlined style={{ color: brandSemantic.warning }} />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: '',
      dataIndex: 'type',
      key: 'typeIcon',
      width: 50,
      render: (type) => getTypeIcon(type),
    },
    {
      title: t('Contact Date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(b.date) - new Date(a.date),
      defaultSortOrder: 'ascend',
      render: (date) => formatDate(date),
    },
    {
      title: t('Client & Supplier Name'),
      dataIndex: 'client',
      key: 'client',
      render: (text, record) => {
        const lead = leads.find((item) => item.id === record.clientId);
        const supplier = suppliers.find((item) => item.id === record.clientId);
        return (
          <Button
            type="link"
            onClick={() => {
              if (lead) openLeadProfile(lead, 'contacts');
              if (!lead && supplier) openSupplierProfile(supplier, 'contacts');
            }}
            style={{ padding: 0 }}
          >
            {text}
          </Button>
        );
      },
    },
    {
      title: t('Counterparty Type'),
      key: 'counterpartyType',
      width: 150,
      render: (_, record) => {
        const lead = leads.find((item) => item.id === record.clientId);
        if (lead) {
          return <ClientTypeTag status={lead.status} />;
        }
        return (
          <SoftTag tone={{ color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder }}>
            {t('Supplier')}
          </SoftTag>
        );
      },
    },
    {
      title: t('Contact Source'),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type) => {
        const colors = {
          Email: 'green',
          Phone: 'blue',
          Network: 'cyan',
          'Fair/Event': 'gold',
          Referral: 'purple',
          Other: 'orange',
        };
        return <Tag color={colors[type] || 'default'}>{t(type)}</Tag>;
      },
    },
    {
      title: t('Subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => showModal('viewContact', record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t('Contact By'),
      dataIndex: 'userName',
      key: 'userName',
      width: 150,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => showModal('editContact', record)}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Contact Record'),
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() { message.success(t('Deleted')); },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Counterparties')}</Title>
        <div className="page-toolbar-controls">
          <Search
            placeholder={t('Search contact records...')}
            style={{ width: 300 }}
            size="large"
            value={contactSearch}
            onChange={(event) => setContactSearch(event.target.value)}
            onSearch={(value) => setContactSearch(value)}
            allowClear
          />
          <Select
            value={contactFilter}
            onChange={setContactFilter}
            style={{ width: 150 }}
            size="large"
          >
            <Option value="my">{t('My Contact Records')}</Option>
            <Option value="all">{t('All Contact Records')}</Option>
          </Select>
          <Select
            value={contactTypeFilter}
            onChange={setContactTypeFilter}
            style={{ width: 120 }}
            size="large"
          >
            <Option value="all">{t('All sources')}</Option>
            <Option value="Email">{t('Email')}</Option>
            <Option value="Phone">{t('Phone')}</Option>
            <Option value="Network">{t('Network')}</Option>
            <Option value="Fair/Event">{t('Fair/Event')}</Option>
            <Option value="Referral">{t('Referral')}</Option>
          </Select>
          <RangePicker
            value={contactDateRange}
            onChange={setContactDateRange}
            style={{ width: 280 }}
            size="large"
            placeholder={[t('Contact date from'), t('Contact date to')]}
          />
          <Button
            size="large"
            style={{ minWidth: 88 }}
            onClick={() => {
              setContactFilter('my');
              setContactTypeFilter('all');
              setContactDateRange([null, null]);
              setContactSearch('');
            }}
          >
            {t('Clear')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ minWidth: 172 }}
            onClick={() => showModal('createContact')}
          >
            {t('Add Contact Record')}
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredContacts}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
      />
    </div>
  );
}
